import { useCallback, useEffect, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import { speakAnswer } from '~/services/OpenAIProxyService';

import { extractSpeakableText } from '~/utils/leData/extractSpeakableText';

export type AnswerSpeech = {
  /** The message currently being played, or null when nothing is playing. */
  speakingId: string | null;
  /** The message whose audio is still being synthesised. */
  preparingId: string | null;
  /** Play the answer, or stop it if this same message is already playing. */
  toggleSpeech: (messageId: string, markdown: string) => Promise<void>;
  stopSpeech: () => void;
};

/**
 * Speaks a finished answer on demand.
 *
 * Deliberately v1-shaped: one answer at a time, whole answer after streaming finishes rather than
 * sentence by sentence, and never autoplay — audio only ever starts from a click, which is also
 * what keeps browsers from blocking it.
 */
export default function useAnswerSpeech(): AnswerSpeech {
  const { enqueueSnackbar } = useSnackbar();
  const [speakingId, setSpeakingId] = useState<string | null>(null);
  const [preparingId, setPreparingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const requestRef = useRef<AbortController | null>(null);

  const releaseAudio = useCallback(() => {
    audioRef.current?.pause();
    audioRef.current = null;

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const stopSpeech = useCallback(() => {
    requestRef.current?.abort();
    requestRef.current = null;
    releaseAudio();
    setSpeakingId(null);
    setPreparingId(null);
  }, [releaseAudio]);

  // Leaving the page mid-answer must not leave audio playing over whatever comes next.
  useEffect(() => stopSpeech, [stopSpeech]);

  const toggleSpeech = useCallback(
    async (messageId: string, markdown: string) => {
      if (speakingId === messageId || preparingId === messageId) {
        stopSpeech();
        return;
      }

      stopSpeech();

      const { text, truncated } = extractSpeakableText(markdown);

      if (!text) {
        enqueueSnackbar('Nothing to read aloud — this answer is all tables and charts', { variant: 'warning' });
        return;
      }

      if (truncated) {
        enqueueSnackbar('Long answer — reading the opening only', { variant: 'info' });
      }

      const controller = new AbortController();
      requestRef.current = controller;
      setPreparingId(messageId);

      try {
        const audioBlob = await speakAnswer(text, controller.signal);

        if (controller.signal.aborted) {
          return;
        }

        const url = URL.createObjectURL(audioBlob);
        const audio = new Audio(url);

        objectUrlRef.current = url;
        audioRef.current = audio;
        audio.onended = () => {
          releaseAudio();
          setSpeakingId(null);
        };
        audio.onerror = () => {
          releaseAudio();
          setSpeakingId(null);
          enqueueSnackbar('Could not play the answer', { variant: 'error' });
        };

        await audio.play();
        setSpeakingId(messageId);
      } catch (error) {
        if (!controller.signal.aborted) {
          enqueueSnackbar(`Could not read the answer aloud: ${(error as Error).message}`, { variant: 'error' });
        }
        releaseAudio();
        setSpeakingId(null);
      } finally {
        if (requestRef.current === controller) {
          requestRef.current = null;
        }
        setPreparingId((current) => (current === messageId ? null : current));
      }
    },
    [enqueueSnackbar, preparingId, releaseAudio, speakingId, stopSpeech],
  );

  return { speakingId, preparingId, toggleSpeech, stopSpeech };
}
